// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import { TippyProps } from "@tippyjs/react";
import { sticky } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import { TippyPopupProps } from "@guestsupply/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useEffect, useState } from "react";

export const defaultProps: TippyProps = {
    placement: "bottom",
    delay: 250,
    maxWidth: "none",
    interactive: true,
    trigger: "mouseenter focus",
    offset: [0, 14],
    animation: "shift-away",
    sticky: "reference",
    plugins: [sticky],
    arrow: true,
    inertia: false,
};

export const defaultMobileProps: TippyProps = {
    ...defaultProps,
    trigger: "click",
    arrow: false,
};

export const defaultLeftSideMobileProps: TippyProps = {
    ...defaultMobileProps,
    animation: "shift-away",
    inertia: true,
    placement: "left-start",
    maxWidth: 300,
};

/**
 * We use "useTippyProps" hook to merge all default props depends on screen size.
 * we give "props" from component high priority
 * @param props
 */
export const useTippyProps = (props: TippyPopupProps) => {
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon?.isTabletScreen,
    }));
    const [options, setOptions] = useState<TippyPopupProps>({});

    const setOptionsOnMobile = () => {
        const isMobileDevice = isTabletScreen;

        const options: TippyPopupProps = {
            ...defaultProps,
            ...(isMobileDevice && props.mobileMode ? defaultMobileProps : {}),
            ...(isMobileDevice && props.mobileMode === "left-side" ? defaultLeftSideMobileProps : {}),
            ...props,
        };

        return options;
    };

    useEffect(() => {
        const options = setOptionsOnMobile();
        setOptions(options);
    }, [isTabletScreen]);

    return options;
};
